/**
 * Sprint M Item 4 + Sprint N P3 — Conversion Banner V2
 * V4.13 Founder-QA — fully i18n'd (was hardcoded German in HR/EN UIs).
 *
 * Reads live counts from `GET /api/insights/daily-summary` and renders
 * a NON-fake banner. NEVER returns null when the API succeeds — we
 * always have *something* to say: bargains today, or fresh inventory
 * today, or "X im Bestand" Marktscan-Hinweis, or a "be the first" prompt.
 * NO fake counters: every number traces back to one server-side query
 * against the materialised feeds. Refresh: every 60 min.
 */
import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Trans, useTranslation } from "react-i18next";
import { Sparkles, Zap, Bell, TrendingUp, Activity } from "lucide-react";
import { apiClient } from "../api/client";
import { useAuth } from "../auth/AuthContext";

async function fetchDailySummary(vertical, country) {
  const params = {};
  if (vertical) params.vertical = vertical;
  if (country) params.country = country;
  const r = await apiClient.get("/insights/daily-summary", { params });
  return r.data;
}

const NUM_LOCALES = { de: "de-AT", hr: "hr-HR", en: "en-GB" };

export default function ConversionBanner({ vertical, country }) {
  const { user } = useAuth();
  const { t, i18n } = useTranslation("dashboard");
  const isPro = !!user?.is_pro;
  const fmt = (n) =>
    (n || 0).toLocaleString(NUM_LOCALES[i18n.language] || "de-AT");

  const { data, isLoading, isError } = useQuery({
    queryKey: ["insights", "daily-summary", vertical || "all", country || "default"],
    queryFn: () => fetchDailySummary(vertical, country),
    staleTime: 60 * 60 * 1000,
    retry: 1,
  });

  if (isLoading || isError || !data) return null;

  const bargains = data.bargains_total || 0;
  const strong = data.bargains_strong || 0;
  const proOnly = data.pro_only || 0;
  const fresh = data.fresh_total || 0;
  const available = data.bargains_available_total || 0;
  const availableStrong = data.bargains_available_strong || 0;

  if (bargains === 0 && fresh === 0 && available === 0) return null;

  // ───── BARGAINS PATH (the "wow" state) ─────────────────────────────
  if (bargains > 0 && isPro) {
    return (
      <div
        data-testid="conversion-banner-pro"
        className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 px-5 py-3.5 flex items-center gap-3"
      >
        <span className="flex h-2.5 w-2.5 rounded-full bg-emerald-400 animate-pulse" />
        <Sparkles size={16} strokeWidth={1.8} className="text-emerald-300" aria-hidden="true" />
        <span className="text-sm text-emerald-200">
          <Trans
            t={t}
            i18nKey="cb.proBargains"
            values={{ bargains: fmt(bargains), strong: fmt(strong) }}
            components={{
              b1: <span className="font-semibold text-emerald-100" data-testid="cb-pro-bargains" />,
              b2: <span className="font-semibold text-emerald-100" data-testid="cb-pro-strong" />,
              accent: <span className="text-emerald-400" />,
            }}
          />
        </span>
      </div>
    );
  }

  if (bargains > 0 && !isPro) {
    return (
      <div
        data-testid="conversion-banner-free"
        className="rounded-2xl border border-amber-500/40 bg-amber-500/5 px-5 py-3.5 flex flex-col sm:flex-row sm:items-center gap-3"
      >
        <div className="flex items-center gap-3 flex-1">
          <span className="flex h-2.5 w-2.5 rounded-full bg-amber-400 animate-pulse" />
          <Sparkles size={16} strokeWidth={1.8} className="text-amber-300" aria-hidden="true" />
          <span className="text-sm text-zinc-100">
            <Trans
              t={t}
              i18nKey="cb.freeBargains"
              values={{ bargains: fmt(bargains) }}
              components={{
                b1: <span className="font-semibold text-amber-200" data-testid="cb-free-bargains" />,
              }}
            />{" "}
            {proOnly > 0 && (
              <Trans
                t={t}
                i18nKey="cb.freeBargainsProOnly"
                values={{ proOnly: fmt(proOnly) }}
                components={{
                  b2: <span className="font-semibold text-amber-300" data-testid="cb-free-pro-only" />,
                }}
              />
            )}
          </span>
        </div>
        <a
          href="/billing"
          data-testid="cb-pro-upgrade-link"
          className="text-xs font-medium px-3 py-1.5 rounded-md bg-amber-500 text-zinc-950 hover:bg-amber-400 transition-colors whitespace-nowrap"
        >
          {t("cb.ctaPro")}
        </a>
      </div>
    );
  }

  // ───── Bestand path (Marktscan pause: fresh=0 but available>0) ──────
  if (fresh === 0 && available > 0) {
    if (isPro) {
      return (
        <div
          data-testid="conversion-banner-pro-bestand"
          className="rounded-2xl border border-sky-500/30 bg-sky-500/5 px-5 py-3.5 flex items-center gap-3"
        >
          <span className="flex h-2.5 w-2.5 rounded-full bg-sky-400 animate-pulse" />
          <Activity size={16} strokeWidth={1.8} className="text-sky-300" aria-hidden="true" />
          <span className="text-sm text-sky-100">
            <Trans
              t={t}
              i18nKey="cb.proBestand"
              values={{ available: fmt(available), availableStrong: fmt(availableStrong) }}
              components={{
                b1: <span className="font-semibold text-sky-50" data-testid="cb-pro-bestand-available" />,
                b2: <span className="font-semibold text-sky-50" data-testid="cb-pro-bestand-strong" />,
              }}
            />
          </span>
        </div>
      );
    }
    return (
      <div
        data-testid="conversion-banner-free-bestand"
        className="rounded-2xl border border-sky-500/30 bg-sky-500/5 px-5 py-3.5 flex flex-col sm:flex-row sm:items-center gap-3"
      >
        <div className="flex items-center gap-3 flex-1">
          <span className="flex h-2.5 w-2.5 rounded-full bg-sky-400 animate-pulse" />
          <Activity size={16} strokeWidth={1.8} className="text-sky-300" aria-hidden="true" />
          <span className="text-sm text-zinc-100">
            <Trans
              t={t}
              i18nKey="cb.freeBestand"
              values={{ available: fmt(available), availableStrong: fmt(availableStrong) }}
              components={{
                b1: <span className="font-semibold text-sky-200" data-testid="cb-free-bestand-available" />,
                b2: <span className="font-semibold text-sky-200" data-testid="cb-free-bestand-strong" />,
              }}
            />
          </span>
        </div>
        <a
          href="/billing"
          data-testid="cb-bestand-cta"
          className="text-xs font-medium px-3 py-1.5 rounded-md bg-sky-500 text-zinc-950 hover:bg-sky-400 transition-colors whitespace-nowrap inline-flex items-center gap-1"
        >
          <Zap size={12} />
          {t("cb.ctaPro")}
        </a>
      </div>
    );
  }

  if (isPro) {
    return (
      <div
        data-testid="conversion-banner-pro-empty"
        className="rounded-2xl border border-zinc-700 bg-zinc-900/60 px-5 py-3.5 flex items-center gap-3"
      >
        <span className="flex h-2.5 w-2.5 rounded-full bg-zinc-500" />
        <TrendingUp size={16} strokeWidth={1.8} className="text-zinc-400" aria-hidden="true" />
        <span className="text-sm text-zinc-300">
          <Trans
            t={t}
            i18nKey="cb.proEmpty"
            values={{ fresh: fmt(fresh) }}
            components={{
              b1: <span className="font-semibold text-zinc-100" data-testid="cb-pro-empty-fresh" />,
            }}
          />
        </span>
      </div>
    );
  }

  return (
    <div
      data-testid="conversion-banner-free-empty"
      className="rounded-2xl border border-zinc-700 bg-gradient-to-r from-zinc-900/60 via-zinc-900/40 to-amber-500/[0.04] px-5 py-3.5 flex flex-col sm:flex-row sm:items-center gap-3"
    >
      <div className="flex items-center gap-3 flex-1">
        <span className="flex h-2.5 w-2.5 rounded-full bg-amber-500" />
        <Bell size={16} strokeWidth={1.8} className="text-amber-300" aria-hidden="true" />
        <span className="text-sm text-zinc-200">
          <Trans
            t={t}
            i18nKey="cb.freeEmpty"
            values={{ fresh: fmt(fresh) }}
            components={{
              b1: <span className="font-semibold text-zinc-100" data-testid="cb-free-empty-fresh" />,
            }}
          />
        </span>
      </div>
      <a
        href="/billing"
        data-testid="cb-free-empty-cta"
        className="text-xs font-medium px-3 py-1.5 rounded-md bg-amber-500 text-zinc-950 hover:bg-amber-400 transition-colors whitespace-nowrap inline-flex items-center gap-1"
      >
        <Zap size={12} />
        {t("cb.ctaTrial")}
      </a>
    </div>
  );
}
